import { useEffect, useRef, useState } from "react";
import { Upload, X, Save, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const inputCls =
  "w-full px-3 py-2.5 text-sm font-medium bg-white border border-[#E5E5E5] rounded-none outline-none transition-all focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A] placeholder:text-[#A1A1AA]";
const textareaCls = inputCls + " resize-none";
const labelCls =
  "text-[10px] font-bold uppercase tracking-[0.18em] text-[#52525B] mb-1.5 block";

export default function SetupWizard({ open, onOpenChange, initial, onSave }) {
  const [draft, setDraft] = useState(initial);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) =>
      setDraft((d) => ({ ...d, logoDataUrl: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = (download = false) => {
    if (!draft.name?.trim()) return;
    onSave?.(draft, download);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="setup-wizard"
        className="sm:max-w-[640px] rounded-none border-2 border-[#0A0A0A] p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 py-5 border-b-2 border-[#0A0A0A] bg-[#FAFAFA]">
          <DialogTitle className="text-xl font-extrabold tracking-tighter">
            One-time Setup · Company Details
          </DialogTitle>
          <DialogDescription className="text-xs text-[#52525B]">
            Fill these once. They flow into every invoice. You can re-open this
            from the header at any time.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-6 space-y-5 max-h-[70vh] overflow-y-auto scroll-clean">
          <div className="flex items-start gap-5">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="setup-logo-btn"
              className="w-24 h-24 border-2 border-dashed border-[#0A0A0A] flex items-center justify-center bg-[#FAFAFA] hover:bg-[#0A0A0A]/5 transition-colors shrink-0"
            >
              {draft.logoDataUrl ? (
                <img
                  src={draft.logoDataUrl}
                  alt="logo"
                  className="w-full h-full object-contain p-2"
                />
              ) : (
                <div className="text-center">
                  <Upload size={20} className="mx-auto mb-1" strokeWidth={2} />
                  <span className="text-[9px] uppercase tracking-widest font-bold">
                    Logo
                  </span>
                </div>
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="setup-logo-input"
              onChange={onLogo}
            />
            <div className="flex-1 grid grid-cols-1 gap-3">
              <div>
                <label className={labelCls}>Business Name *</label>
                <input
                  data-testid="setup-name-input"
                  className={inputCls}
                  placeholder="Acme Trading Co."
                  value={draft.name}
                  onChange={(e) =>
                    setDraft({ ...draft, name: e.target.value })
                  }
                  autoFocus
                />
              </div>
              <div>
                <label className={labelCls}>GSTIN / Tax ID</label>
                <input
                  data-testid="setup-gstin-input"
                  className={inputCls}
                  placeholder="29ABCDE1234F1Z5"
                  value={draft.gstin}
                  onChange={(e) =>
                    setDraft({ ...draft, gstin: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Phone</label>
              <input
                data-testid="setup-phone-input"
                className={inputCls}
                placeholder="+91 98765 43210"
                value={draft.phone}
                onChange={(e) =>
                  setDraft({ ...draft, phone: e.target.value })
                }
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                data-testid="setup-email-input"
                className={inputCls}
                placeholder="hello@acme.in"
                value={draft.email}
                onChange={(e) =>
                  setDraft({ ...draft, email: e.target.value })
                }
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Address</label>
              <textarea
                data-testid="setup-address-input"
                rows={2}
                className={textareaCls}
                placeholder="221B, MG Road, Bengaluru 560001"
                value={draft.address}
                onChange={(e) =>
                  setDraft({ ...draft, address: e.target.value })
                }
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Tagline (optional)</label>
              <input
                data-testid="setup-tagline-input"
                className={inputCls}
                placeholder="Quality Hardware Since 1998"
                value={draft.tagline || ""}
                onChange={(e) =>
                  setDraft({ ...draft, tagline: e.target.value })
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-2 border-t border-[#E5E5E5]">
            <div>
              <label className={labelCls}>Default Tax Label</label>
              <input
                data-testid="setup-tax-label-input"
                className={inputCls}
                placeholder="GST"
                value={draft.taxLabel}
                onChange={(e) =>
                  setDraft({ ...draft, taxLabel: e.target.value })
                }
              />
            </div>
            <div>
              <label className={labelCls}>Default Tax %</label>
              <input
                data-testid="setup-tax-rate-input"
                type="number"
                min="0"
                max="100"
                step="any"
                className={inputCls}
                value={draft.taxRate}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    taxRate:
                      e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
              />
            </div>
            <div>
              <label className={labelCls}>Default Unit</label>
              <input
                data-testid="setup-default-unit-input"
                className={inputCls}
                placeholder="PCS"
                value={draft.defaultUnit || ""}
                onChange={(e) =>
                  setDraft({ ...draft, defaultUnit: e.target.value })
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 pt-2 border-t border-[#E5E5E5]">
            <div>
              <label className={labelCls}>Payment Details (optional)</label>
              <textarea
                data-testid="setup-payment-input"
                rows={3}
                className={textareaCls}
                placeholder="A/c Name: Acme Trading Co.\nA/c No: 1234567890\nIFSC: HDFC0000123\nUPI: acme@upi"
                value={draft.paymentDetails || ""}
                onChange={(e) =>
                  setDraft({ ...draft, paymentDetails: e.target.value })
                }
              />
            </div>
            <div>
              <label className={labelCls}>Default Notes (optional)</label>
              <textarea
                data-testid="setup-notes-input"
                rows={2}
                className={textareaCls}
                placeholder="Thank you for your business."
                value={draft.notes || ""}
                onChange={(e) =>
                  setDraft({ ...draft, notes: e.target.value })
                }
              />
            </div>
            <div>
              <label className={labelCls}>Default Terms (optional)</label>
              <textarea
                data-testid="setup-terms-input"
                rows={2}
                className={textareaCls}
                placeholder="Payment due within 7 days."
                value={draft.terms || ""}
                onChange={(e) =>
                  setDraft({ ...draft, terms: e.target.value })
                }
              />
            </div>
          </div>

          {draft.logoDataUrl && (
            <button
              type="button"
              onClick={() => setDraft({ ...draft, logoDataUrl: "" })}
              data-testid="setup-logo-remove-btn"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#52525B] hover:text-[#FF3B30]"
            >
              <X size={12} /> Remove Logo
            </button>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t-2 border-[#0A0A0A] bg-[#FAFAFA] flex flex-row sm:flex-row sm:justify-between gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="setup-cancel-btn"
            className="inline-flex items-center gap-1.5 border border-[#0A0A0A] bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            Cancel
          </button>
          <div className="flex flex-wrap gap-2 ml-auto">
            <button
              type="button"
              onClick={() => handleSave(true)}
              data-testid="setup-save-export-btn"
              disabled={!draft.name?.trim()}
              className="inline-flex items-center gap-1.5 border border-[#0A0A0A] bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download size={13} strokeWidth={2.5} />
              Save & Download Config
            </button>
            <button
              type="button"
              onClick={() => handleSave(false)}
              data-testid="setup-save-btn"
              disabled={!draft.name?.trim()}
              className="inline-flex items-center gap-1.5 bg-[#002FA7] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#00227A] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Save size={13} strokeWidth={2.5} />
              Save
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
